/**
 * @file api-carriers-sdek.ts
 * @description API-клиент СДЭК-интеграции для shop-кабинета (phase10.5 шаг 5).
 *
 * Архитектура (решение 2026-05-29):
 *  - Подключение интеграции (apiKey/clientSecret/...) — в platform-кабинете
 *    через api-platform-integrations.ts (kind=SDEK).
 *  - Управление (отправка отгрузки в СДЭК, выбор ПВЗ, sync статуса) — этот файл.
 */

import { apiDownload, apiFetch, type ApiFileResponse } from "./api-client";
import type { DeliveryStage } from "./api-delivery-monitoring";

export type CarrierKind = "SDEK";

export interface PickupPointDto {
  id: string;
  carrier: CarrierKind;
  externalCode: string;
  city: string;
  region: string | null;
  address: string;
  lat: number | null;
  lng: number | null;
  workingHours: string | null;
  /** Контакты пункта из справочника СДЭК (могут отсутствовать). */
  phone: string | null;
  email: string | null;
  hasCashPay: boolean;
  isActive: boolean;
}

export interface SendShipmentResult {
  shipmentId: string;
  trackNumber: string | null;
  externalCarrierUuid: string;
  externalCarrierOrderId: string | null;
  mock: boolean;
}

/** Получить список ПВЗ СДЭК с фильтром по городу/региону. */
export async function listSdekPickupPoints(params: {
  city?: string;
  region?: string;
} = {}): Promise<PickupPointDto[]> {
  const search = new URLSearchParams();
  if (params.city) search.set("city", params.city);
  if (params.region) search.set("region", params.region);
  const qs = search.toString();
  return apiFetch<PickupPointDto[]>(
    `/admin/carriers/sdek/pickup-points${qs ? `?${qs}` : ""}`,
  );
}

/** Ручной триггер sync ПВЗ из API СДЭК → локальный cache. */
export async function syncSdekPickupPoints(params: {
  city?: string;
  region?: string;
} = {}): Promise<{ upserted: number; mock: boolean }> {
  return apiFetch(`/admin/carriers/sdek/pickup-points/sync`, {
    method: "POST",
    json: params,
  });
}

/** Передать отгрузку в СДЭК — создать заказ на доставку. */
export async function sendShipmentToSdek(shipmentId: string): Promise<SendShipmentResult> {
  return apiFetch<SendShipmentResult>(
    `/admin/shipments/${shipmentId}/send-to-carrier`,
    { method: "POST" },
  );
}

/**
 * Результат опроса статуса заявки в СДЭК (phase19.12 шаг 6; итоги ленты —
 * phase19.13, финальный батч).
 *
 * `addedEvents`/`currentStage`/`lastEventAt` считает сервер, поэтому админке
 * больше не нужно читать ленту до и после опроса, чтобы написать честный тост.
 */
export interface SyncShipmentStatusResult {
  updated: boolean;
  mock: boolean;
  /**
   * СДЭК отклонил заявку асинхронно (например, не подошёл ПВЗ). Бэкенд уже
   * сбросил заявку — отгрузка снова черновик, её можно передать заново.
   */
  rejected: boolean;
  rejectionErrors?: string[];
  /** Сколько событий добавилось в ленту этим вызовом (0 — перевозчик молчит). */
  addedEvents: number;
  /** Этап новейшего события ленты; null — событий по отгрузке ещё нет. */
  currentStage: DeliveryStage | null;
  /** Момент новейшего события ленты (ISO); null — событий нет. */
  lastEventAt: string | null;
}

/** Принудительно обновить статус отгрузки из СДЭК (пуллинг). */
export async function syncSdekShipmentStatus(
  shipmentId: string,
): Promise<SyncShipmentStatusResult> {
  return apiFetch(`/admin/shipments/${shipmentId}/sync-status`, { method: "POST" });
}

// ─── Печатные формы (phase19.12 шаг 6) ──────────────────────────────────────

/**
 * Печатные формы отгрузки. Реестр: новая форма — запись здесь, кнопки в
 * карточке отгрузки рисуются по нему.
 */
export const SHIPMENT_PRINT_FORMS = [
  { key: "receipt", label: "Квитанция PDF", filePrefix: "cdek-receipt" },
  { key: "barcode", label: "ШК мест PDF", filePrefix: "cdek-barcode" },
] as const;

export type ShipmentPrintForm = (typeof SHIPMENT_PRINT_FORMS)[number]["key"];

/**
 * Скачать печатную форму отгрузки.
 *
 * Возможные ошибки сервера: 422 `shipment_not_sent` (отгрузка ещё не передана
 * или mock-режим), 503 `sdek_print_not_ready` (СДЭК готовит форму — повторить
 * через минуту). Текст ошибки показываем как есть.
 */
export async function downloadShipmentPrintForm(
  shipmentId: string,
  form: ShipmentPrintForm,
): Promise<ApiFileResponse> {
  const meta = SHIPMENT_PRINT_FORMS.find((f) => f.key === form);
  return apiDownload(
    `/admin/shipments/${shipmentId}/print/${form}`,
    { headers: { Accept: "application/pdf" } },
    `${meta?.filePrefix ?? form}-${shipmentId}.pdf`,
  );
}

/** Ссылка для отслеживания на сайте СДЭК. */
export function sdekTrackingUrl(trackNumber: string): string {
  return `https://www.cdek.ru/ru/tracking?order_id=${encodeURIComponent(trackNumber)}`;
}
